import type { CSSProperties } from 'react';
import type { ApiError } from '../../api/envelope';
import type { DispatchFeedAnswer, DispatchMetric, DispatchMetricData } from '../../api/types';
import { type Delta, delta, exact, money } from '../analytics/analyticsFormat';
import { answerFor, cardTitle, feedSeries } from './cards';
import { type Bucket, type CivilDay, type ResolvedFrame, dayString } from './frames';
import { isSnapshotMetric, metricTypeOf } from './metricMeta';
import { type ChartData, buildChart, buildKpi } from './series';

/**
 * What one card SAYS, decided once for every layout that draws it.
 *
 * The desktop grid and the phone screen draw a card very differently, and
 * they must never disagree about what is on it: which figure, which change,
 * whether it failed, whether it is still loading. Two renderers each working
 * that out for themselves is two chances to get the order of the checks
 * wrong — and the order is the whole point. So the answer is data, computed
 * here, and each layout only decides how it looks.
 *
 * Four different empties, and they must never look alike: a read that failed
 * (`failed`), a category that refused (`refused`), a question that was not
 * asked or has no answer to give (`unavailable`, a reason rather than a
 * failure), and a window with nothing in it — which is a real `kpi` of zero or
 * a real, empty `chart`. Rendering any of the first three as a zero is the
 * failure this screen is most exposed to: an operator reading "0 delayed" off
 * a card that never managed to look.
 */

/**
 * A KPI figure's length, for its CSS to fit it to the card (`.db-figure`,
 * `.dbp-figure`). Money runs as long as the company's sign makes it, and
 * "COL$40,000,000" set in the type "684" is drawn in spills out of the card —
 * or, on a phone, wraps in the middle of the number.
 */
export const figureFit = (figure: string): CSSProperties =>
  ({ '--figure-chars': Math.max(1, figure.length) }) as CSSProperties;

/** As much of a load's state as a card reads (see lib/useLoad). */
export interface Answered<T> {
  data: T | null;
  error: ApiError | null;
  /** Loading because the question changed: `data` is the previous answer. */
  stale: boolean;
}

/** The page's reads and its window, which every card is drawn from. */
export interface CardInputs {
  frame: ResolvedFrame;
  zone: string | null;
  /** What a chart is drawn over — the window, or the week behind a single day. */
  context: { from: CivilDay; to: CivilDay; widened: boolean };
  contextBucket: Bucket;
  window: Answered<DispatchMetricData[]>;
  compare: Answered<DispatchMetricData[]>;
  snapshot: Answered<DispatchMetricData[]>;
  /** The widened range's read, for charts over a single day. */
  contextRead: Answered<DispatchMetricData[]>;
  feed: Answered<DispatchFeedAnswer>;
  /** The board read over the widened range, for dispatch cards drawn as charts. */
  feedContext: Answered<DispatchFeedAnswer>;
  /** The metrics counted from the job board rather than the metrics API. */
  feedIds: ReadonlySet<number>;
}

/** Under a KPI's figure: the change against the window before, or why there is none. */
export type KpiFoot = { kind: 'delta'; delta: Delta } | { kind: 'note'; text: string };

export type CardFace =
  | { kind: 'loading' }
  /** The read itself failed — a stated failure, in the load's own words. */
  | { kind: 'failed'; error: ApiError }
  /** dispatch answered this card's category with a refusal. */
  | { kind: 'refused'; message: string }
  /** No figure to give, and a reason that is not a failure. */
  | { kind: 'unavailable'; message: string }
  | {
      kind: 'kpi';
      figure: string;
      foot: KpiFoot;
      /** The window's shape under the figure, or null where one would say nothing. */
      spark: ChartData | null;
      /** What the change is measured against, or what the figure is OF. */
      caption: string | null;
    }
  | {
      kind: 'chart';
      line: boolean;
      chart: ChartData;
      /** The bucket the operator asked for, when a week is drawn around it. */
      highlight: string | null;
      /** Why the chart is wider than the window, when it is. */
      note: string | null;
    };

const WIDENED_NOTE = 'week shown for context';

const figureOf = (value: number, kind: 'currency' | 'count'): string =>
  kind === 'currency' ? money(value) : exact(value);

/**
 * A dispatch card, counted from the job board.
 *
 * Its figure is the board's own count — a union over its buckets — and the
 * spark and bars are that same set of jobs by pickup day, so the headline is
 * the sum of the bars under it. There is no comparison: the board is whole job
 * models, and a previous window would double what this screen costs dispatch
 * for a percentage the Flutter page has never shown.
 */
const feedFace = (metric: DispatchMetric, inputs: CardInputs): CardFace => {
  const { feed, feedContext, frame, zone, context, contextBucket } = inputs;
  if (feed.error) return { kind: 'failed', error: feed.error };
  // Asked for more of the board than it is read at a time: a reason, not a
  // failure and never a zero, because narrowing the window fixes it.
  if (feed.data?.unavailable) return { kind: 'unavailable', message: feed.data.unavailable };
  if (feed.stale) return { kind: 'loading' };

  const figure = feed.data?.payload.find((entry) => entry.id === metric.id);
  if (!figure) return { kind: 'unavailable', message: 'This card was not counted.' };
  // Every bucket it names belongs to another category, so the board has
  // nothing to match it against — which is what the dispatch dashboard shows
  // for the same card, and is not the same as a quiet day.
  if (!figure.codes.length) return { kind: 'unavailable', message: 'No dispatch statuses on this card.' };

  const title = cardTitle(metric);
  const { code, rows } = feedSeries(figure, title);
  if (metricTypeOf(metric.type) === 'kpi') {
    return {
      kind: 'kpi',
      figure: exact(figure.count),
      // Only the money, where the card asks for any: a payment card's dollars.
      foot: { kind: 'note', text: figure.amount === null ? '' : money(figure.amount) },
      // Sparked over the page's window, like its count: a spark over the
      // widened week beside it would be two answers on one card.
      spark:
        figure.count > 0
          ? buildChart({ rows, codes: [code], from: frame.from, to: frame.to, bucket: frame.bucket, zone })
          : null,
      // What the number is OF: the board as it stands, which the page's window
      // does not narrow the way it narrows every other card.
      caption: 'On the board now',
    };
  }

  // A chart is all shape, so it takes the widened board where there is one —
  // a single full-width block is not a reading of anything.
  const wide = context.widened
    ? (feedContext.data?.payload.find((entry) => entry.id === metric.id) ?? null)
    : null;
  const drawn = wide ? feedSeries(wide, title) : { code, rows };
  return {
    kind: 'chart',
    line: metricTypeOf(metric.type) === 'lineChart',
    chart: buildChart({
      rows: drawn.rows,
      codes: [drawn.code],
      from: wide ? context.from : frame.from,
      to: wide ? context.to : frame.to,
      bucket: wide ? contextBucket : frame.bucket,
      zone,
    }),
    highlight: wide ? dayString(frame.from) : null,
    note: wide ? WIDENED_NOTE : null,
  };
};

/**
 * A card's chart, over the context range rather than the window when the two
 * differ. The highlighted bucket is the day the operator actually asked for —
 * with a week drawn behind it, the figure they wanted must still be the one
 * picked out, or the chart is answering a question nobody put.
 */
export const chartOver = (
  inputs: CardInputs,
  rows: DispatchMetricData['series'],
  codes: string[],
  category: string,
): { chart: ChartData; highlight: string | null; widened: boolean } => {
  const { context, contextBucket, contextRead, frame, zone } = inputs;
  const wide = context.widened;
  const source = wide ? answerFor(contextRead.data, category).series : rows;
  return {
    chart: buildChart({ rows: source, codes, from: context.from, to: context.to, bucket: contextBucket, zone }),
    highlight: wide ? dayString(frame.from) : null,
    widened: wide,
  };
};

export const cardFace = (metric: DispatchMetric, inputs: CardInputs): CardFace => {
  if (inputs.feedIds.has(metric.id)) return feedFace(metric, inputs);

  const codes = metric.subCategory.filter(Boolean);
  const snapshot = isSnapshotMetric(codes);
  // No window at all: an all-time figure is not narrowed by the page's range.
  const source = snapshot ? inputs.snapshot : inputs.window;
  if (source.error) return { kind: 'failed', error: source.error };
  const answer = answerFor(source.data, metric.category);
  if (answer.error) return { kind: 'refused', message: answer.error };
  if (source.stale) return { kind: 'loading' };

  if (metricTypeOf(metric.type) === 'kpi') {
    const kpi = buildKpi({
      rows: answer.series,
      compareRows: snapshot ? null : answerFor(inputs.compare.data, metric.category).series,
      codes,
      snapshot,
    });
    return {
      kind: 'kpi',
      figure: figureOf(kpi.current, kpi.kind),
      foot:
        kpi.previous === null
          ? { kind: 'note', text: snapshot ? 'All time, right now' : 'No comparison' }
          : { kind: 'delta', delta: delta(kpi.current, kpi.previous) },
      // A snapshot has no window to spark, and a one-day window sparks the
      // week behind it — one point is a dot, not a shape.
      spark: snapshot ? null : chartOver(inputs, answer.series, codes, metric.category).chart,
      caption: kpi.previous === null ? null : inputs.frame.compareLabel,
    };
  }

  const drawn = chartOver(inputs, answer.series, codes, metric.category);
  return {
    kind: 'chart',
    line: metricTypeOf(metric.type) === 'lineChart',
    chart: drawn.chart,
    highlight: drawn.highlight,
    // The readout names the day and its figures; this says why a week is
    // drawn around it. A band with nothing naming it is decoration.
    note: drawn.widened ? WIDENED_NOTE : null,
  };
};
